import {
	BrokerCommunication,
	ContractCounts,
	ContractFilters,
	ContractsList,
	PaginatedResponse,
	PropositionDocument,
	Contract,
	Task,
} from "@dune/types";
import { useQuery } from "@tanstack/react-query";
import { privateAxios } from "../axios";

const getContracts = async (
	filters: ContractFilters,
): Promise<ContractsList> => {
	const { data } = await privateAxios.get<ContractsList>(
		"/propositions/contracts",
		{
			params: filters,
		},
	);
	return data;
};

const getContractById = async (id: number) => {
	const { data } = await privateAxios.get<Contract>(
		`/propositions/contracts/${id}`,
	);
	return data;
};

const getContractCommunications = async (
	contractId: number,
	filters: {
		page?: number;
		limit?: number;
		sortBy?: string;
		sortOrder?: "asc" | "desc";
	},
): Promise<PaginatedResponse<BrokerCommunication>> => {
	const { data } = await privateAxios.get<
		PaginatedResponse<BrokerCommunication>
	>(`/propositions/contracts/${contractId}/communications`, {
		params: filters,
	});
	return data;
};

const getContractsCounts = async (): Promise<ContractCounts> => {
	const { data } = await privateAxios.get<ContractCounts>(
		"/propositions/contracts/counts",
	);
	return data;
};

const getContractTasks = async (contractId: number) => {
	const { data } = await privateAxios.get<Task[]>(
		`/propositions/contracts/${contractId}/tasks`,
	);
	return data;
};

const getContractDocuments = async (
	contractId: number,
): Promise<PropositionDocument[]> => {
	const { data } = await privateAxios.get<PropositionDocument[]>(
		`/propositions/contracts/${contractId}/documents`,
	);
	return data;
};

const getContractUnprocessedCommunicationsCount = async (
	contractId: number,
): Promise<number> => {
	const { data } = await privateAxios.get<number>(
		`/propositions/contracts/${contractId}/communications/unprocessed-count`,
	);
	return data;
};

export const contractsKeys = {
	all: ["contracts"] as const,
	lists: () => [...contractsKeys.all, "list"] as const,
	list: (filters: ContractFilters) =>
		[...contractsKeys.lists(), filters] as const,
	details: () => [...contractsKeys.all, "detail"] as const,
	detail: (id: number) => [...contractsKeys.details(), id] as const,
	communications: (
		id: number,
		filters: {
			page?: number;
			limit?: number;
			sortBy?: string;
			sortOrder?: "asc" | "desc";
		},
	) => [...contractsKeys.detail(id), "communications", filters] as const,
	counts: () => [...contractsKeys.all, "counts"] as const,
	tasks: (id: number) => [...contractsKeys.detail(id), "tasks"] as const,
	unprocessedCommunicationsCount: (id: number) =>
		[...contractsKeys.detail(id), "unprocessed-communications-count"] as const,
};

export const useContracts = (filters: ContractFilters) => {
	return useQuery({
		queryKey: contractsKeys.list(filters),
		queryFn: () => getContracts(filters),
		placeholderData: (previousData) => previousData,
	});
};

export const useContract = (id: number) => {
	return useQuery({
		queryKey: contractsKeys.detail(id),
		queryFn: () => getContractById(id),
		enabled: !!id,
	});
};

export const useContractCommunications = (
	contractId: number,
	filters: {
		page?: number;
		limit?: number;
		sortBy?: string;
		sortOrder?: "asc" | "desc";
	},
) => {
	return useQuery({
		queryKey: contractsKeys.communications(contractId, filters),
		queryFn: () => getContractCommunications(contractId, filters),
		enabled: !!contractId,
		placeholderData: (previousData) => previousData,
	});
};

export const useContractsCounts = () => {
	return useQuery({
		queryKey: contractsKeys.counts(),
		queryFn: () => getContractsCounts(),
		placeholderData: (previousData) => previousData,
	});
};

export const useContractTasks = (contractId: number) => {
	return useQuery({
		queryKey: contractsKeys.tasks(contractId),
		queryFn: () => getContractTasks(contractId),
		enabled: !!contractId,
		placeholderData: (previousData) => previousData,
	});
};

export const useContractDocuments = (contractId: number) => {
	return useQuery({
		queryKey: [...contractsKeys.detail(contractId), "documents"],
		queryFn: () => getContractDocuments(contractId),
		enabled: !!contractId,
	});
};

export const useContractUnprocessedCommunicationsCount = (
	contractId: number,
) => {
	return useQuery({
		queryKey: contractsKeys.unprocessedCommunicationsCount(contractId),
		queryFn: () => getContractUnprocessedCommunicationsCount(contractId),
		enabled: !!contractId,
		placeholderData: (previousData) => previousData,
	});
};
